import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { stockApi } from '../../api/stock-api';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  adjusterCommands,
  stockCommandLabels,
  validateCommand,
  type StockCommand,
  type StockCommandKind,
  type StockCommandResult,
  type StockItem,
} from '../../domain/stock';
import { isUnknownOutcome, stockErrorMessage } from './error-message';
import type { StockReferences } from './use-stock-references';

const intros: Record<StockCommandKind, string> = {
  receive: 'Goods that arrived. Adds stock at the location.',
  transfer: 'Moves stock from one location to another.',
  return: 'Unused goods coming back, e.g. from the field. Adds stock at the location.',
  adjust: 'Corrects a balance after a count or damage.',
  openingBalance: 'A counted starting quantity, e.g. when a shelf is first set up.',
};

/**
 * One stock command. The idempotency key is fixed for the dialog's life:
 * after a network failure the form locks and can only resend the same
 * request, so a command is never applied twice.
 */
export function StockCommandDialog({
  kind,
  item,
  references,
  defaultLocationId,
  onClose,
  onDone,
}: {
  kind: StockCommandKind;
  item: StockItem;
  references: StockReferences;
  defaultLocationId?: string | undefined;
  onClose: () => void;
  onDone: (result: StockCommandResult) => void;
}) {
  const queryClient = useQueryClient();
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [locationId, setLocationId] = useState(defaultLocationId ?? '');
  const [toLocationId, setToLocationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [direction, setDirection] = useState<'add' | 'remove'>('remove');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<StockCommand | null>(null);

  const unit = references.unit(item.id);
  const balanceAt = (id: string) =>
    references.balances.find((b) => b.stockItemId === item.id && b.locationId === id);
  const locationOptions = useMemo(
    () =>
      references.locations
        .filter((l) => l.active)
        .map((l) => {
          const balance = references.balances.find(
            (b) => b.stockItemId === item.id && b.locationId === l.id,
          );
          return {
            value: l.id,
            label: `${references.locationName(l.id)}${balance ? ` · ${balance.available} ${unit} available` : ''}`,
            aliases: [l.code, references.siteName(l.siteId)],
          };
        }),
    [references, item.id, unit],
  );

  const run = useMutation({
    mutationFn: (command: StockCommand) => stockApi.recordStockCommand(command),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['stock'] });
      await queryClient.invalidateQueries({ queryKey: ['stock-balances'] });
      onDone(result);
    },
    onError: (failure: unknown, command) => {
      if (isUnknownOutcome(failure)) {
        setPending(command);
        setError(
          'The request could not be confirmed. Retry sends the same request; it is never applied twice.',
        );
      } else {
        setPending(null);
        setError(
          stockErrorMessage(failure, 'The stock change could not be recorded.', 'stock item'),
        );
      }
    },
  });

  function build(): StockCommand {
    const qty = Number.parseInt(quantity, 10);
    const base = { stockItemId: item.id, note: note.trim() || undefined, idempotencyKey };
    switch (kind) {
      case 'transfer':
        return { ...base, kind, fromLocationId: locationId, toLocationId, quantity: qty };
      case 'adjust':
        return { ...base, kind, locationId, quantityDelta: direction === 'add' ? qty : -qty };
      default:
        return {
          ...base,
          kind,
          locationId,
          quantity: qty,
          referenceType: reference.trim() ? 'REFERENCE' : undefined,
          referenceId: reference.trim() || undefined,
        };
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pending) {
      run.mutate(pending);
      return;
    }
    setError(null);
    const command = build();
    const problem = validateCommand(command);
    if (problem) {
      setError(problem);
      return;
    }
    run.mutate(command);
  }

  const locked = pending !== null || run.isPending;
  const title = `${stockCommandLabels[kind]}: ${item.displayName}`;
  const from = locationId ? balanceAt(locationId) : undefined;

  return (
    <div className="drawer-overlay centered-overlay" role="presentation" onClick={onClose}>
      <form
        className="confirmation-dialog stock-command-dialog form-surface"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <div className="drawer-heading">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close stock dialog"
          >
            ×
          </button>
        </div>
        <p className="field-hint">{intros[kind]}</p>
        <fieldset disabled={locked} className="plain-fieldset">
          <label>
            {kind === 'transfer' ? 'From' : 'Location'}
            <SearchableSelect
              ariaLabel={kind === 'transfer' ? 'From location' : 'Location'}
              value={locationId}
              onChange={setLocationId}
              options={locationOptions}
              placeholder="Choose a location"
            />
          </label>
          {kind === 'transfer' ? (
            <label>
              To
              <SearchableSelect
                ariaLabel="To location"
                value={toLocationId}
                onChange={setToLocationId}
                options={locationOptions.filter((option) => option.value !== locationId)}
                placeholder="Choose a location"
              />
            </label>
          ) : null}
          {kind === 'adjust' ? (
            <div className="radio-row" role="radiogroup" aria-label="Adjustment direction">
              <label className="radio-field">
                <input
                  type="radio"
                  name="direction"
                  checked={direction === 'remove'}
                  onChange={() => setDirection('remove')}
                />
                Remove (e.g. damaged or missing)
              </label>
              <label className="radio-field">
                <input
                  type="radio"
                  name="direction"
                  checked={direction === 'add'}
                  onChange={() => setDirection('add')}
                />
                Add (found more than recorded)
              </label>
            </div>
          ) : null}
          <label className="quantity-field">
            Quantity ({unit})
            <input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </label>
          {(kind === 'transfer' || (kind === 'adjust' && direction === 'remove')) && from ? (
            <p className="field-hint">
              {from.available} {unit} available there ({from.allocated} reserved for orders).
            </p>
          ) : null}
          {kind === 'receive' || kind === 'return' || kind === 'openingBalance' ? (
            <label>
              {kind === 'receive' ? 'Delivery note or reference' : 'Reference'}
              <input
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                placeholder="Optional"
              />
            </label>
          ) : null}
          <label>
            {kind === 'adjust' ? 'Why was the balance wrong? (required)' : 'Note'}
            <textarea value={note} onChange={(event) => setNote(event.target.value)} rows={2} />
          </label>
        </fieldset>
        {adjusterCommands.includes(kind) ? (
          <p className="field-hint">Recorded with your name, so the change can be traced later.</p>
        ) : null}
        {error ? (
          <p role="alert" className={pending ? 'form-error partial-failure-inline' : 'form-error'}>
            {error}
          </p>
        ) : null}
        <div className="drawer-actions">
          <button type="button" onClick={onClose}>
            {pending ? 'Close and check' : 'Cancel'}
          </button>
          <button type="submit" className="primary" disabled={run.isPending}>
            {run.isPending
              ? 'Recording…'
              : pending
                ? 'Retry the same request'
                : stockCommandLabels[kind]}
          </button>
        </div>
      </form>
    </div>
  );
}
